import type { SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import type { ObjectiveStatusOrDraft } from '@/utils/objectiveStatus';

import { ATTRIBUTE_BUCKETS, Filters } from '@/constants/Filters';
import { defaultFiltersState } from '@/hooks/filters/useCompanyAttributesFilters';
import { setOrDelete } from '@/hooks/utils/useReplaceSearch';
import { pickObjectiveStatuses } from '@/utils/objectiveStatus';

/**
 * The filters of an objectives list as its address holds them, beside the period lens
 * (`ObjectivePeriodLens`) and the pillar scope (`PillarScope`). Each attribute bucket of a
 * `SelectedFilters` is a parameter of the same name; a list is its ids, comma-separated.
 */
export const SEARCH_PARAM = 'search';
export const STATUS_PARAM = 'status';

const LIST_SEPARATOR = ',';

export const readList = (params: URLSearchParams, key: string): string[] =>
    (params.get(key) ?? '').split(LIST_SEPARATOR).filter(Boolean);

export const writeList = (params: URLSearchParams, key: string, values: string[]) =>
    setOrDelete(params, key, values.join(LIST_SEPARATOR));

/**
 * The states the address picks; a value that names none -- a stale or mistyped link's -- is left
 * out.
 */
export const readStatuses = (params: URLSearchParams): ObjectiveStatusOrDraft[] =>
    pickObjectiveStatuses(readList(params, STATUS_PARAM));

/** Whether the list keeps anything less than all it holds: a name, a state or an attribute. */
export const hasActiveFilterIn = (params: URLSearchParams): boolean =>
    readStatuses(params).length > 0 ||
    [SEARCH_PARAM, ...ATTRIBUTE_BUCKETS].some((key) => !!params.get(key)?.trim());

/** Takes every list filter off the address: the name searched, the states and the attributes. */
export const clearListFilters = (params: URLSearchParams) =>
    [SEARCH_PARAM, STATUS_PARAM, ...ATTRIBUTE_BUCKETS].forEach((key) => params.delete(key));

export const readManagerIds = (params: URLSearchParams): string[] =>
    readList(params, Filters.MANAGERS);

export const writeManagerIds = (params: URLSearchParams, managerIds: string[]) =>
    writeList(params, Filters.MANAGERS, managerIds);

const pickedIds = (values: SelectedFilters[string] | undefined): string[] =>
    (values ?? []).map((value) => value.id).filter((id): id is string => !!id);

export const writeAttributeFilters = (params: URLSearchParams, filters: SelectedFilters) =>
    ATTRIBUTE_BUCKETS.forEach((bucket) => writeList(params, bucket, pickedIds(filters[bucket])));

const valuesOf = (bucket: string, ids: string[]): SelectedFilters[string] =>
    bucket === Filters.MANAGERS
        ? ids.map((id) => ({ id, name: '' }))
        : ids.map((id) => ({ id, names: {} }));

/**
 * The attribute selection the address names, by id alone: the filter control matches its values by
 * id, and no reader of the selection displays a manager's empty name. `undefined` when the address
 * names no attribute at all.
 */
export const readAttributeFilters = (params: URLSearchParams): SelectedFilters | undefined => {
    if (!ATTRIBUTE_BUCKETS.some((bucket) => params.has(bucket))) {
        return undefined;
    }

    return {
        ...defaultFiltersState,
        ...Object.fromEntries(
            ATTRIBUTE_BUCKETS.map((bucket) => [bucket, valuesOf(bucket, readList(params, bucket))]),
        ),
    };
};
